import { useState } from "react";
import { t, tf } from "@/lib/i18n";
import { Newspaper, Milk, Apple, Battery, Package, Coffee, Smartphone, Wine, Leaf, Pill, CupSoda, Wrench, Trophy, RotateCcw } from "lucide-react";

const BINS = [
  { key: "dry", label: "Dry · Recyclable", hint: "Give to your kabadiwala", color: "bg-[var(--leaf)]" },
  { key: "wet", label: "Wet · Compost", hint: "Kitchen & garden", color: "bg-[#8D6E3F]" },
  { key: "haz", label: "Hazardous", hint: "Keep separate", color: "bg-[var(--terracotta)]" },
];

const ITEMS = [
  { name: "Old newspaper", icon: Newspaper, bin: "dry", why: "Tie it in bundles — kabadiwalas buy it." },
  { name: "Rinsed milk pouch", icon: Milk, bin: "dry", why: "Clean & dry plastic is recyclable." },
  { name: "Apple core", icon: Apple, bin: "wet", why: "Food scraps make great compost." },
  { name: "AA battery", icon: Battery, bin: "haz", why: "Batteries leak chemicals. Store separately." },
  { name: "Cardboard box", icon: Package, bin: "dry", why: "Flatten it to save space." },
  { name: "Used tea leaves", icon: Coffee, bin: "wet", why: "Your plants will love them." },
  { name: "Old phone", icon: Smartphone, bin: "dry", why: "E-waste — give it to an e-waste collector or kabadiwala." },
  { name: "Glass bottle", icon: Wine, bin: "dry", why: "Glass can be recycled endlessly." },
  { name: "Dry leaves", icon: Leaf, bin: "wet", why: "Compost them instead of burning." },
  { name: "Expired medicines", icon: Pill, bin: "haz", why: "Return to a chemist take-back, never the drain." },
  { name: "Soda can", icon: CupSoda, bin: "dry", why: "Aluminium is one of the most valuable scraps." },
  { name: "Rusty iron tool", icon: Wrench, bin: "dry", why: "Iron scrap is bought by weight." },
];

const shuffle = (a) => [...a].sort(() => Math.random() - 0.5).slice(0, 8);

export default function SortGame() {
  const [deck, setDeck] = useState(() => shuffle(ITEMS));
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const item = deck[i];
  const done = i >= deck.length;
  const best = Number(localStorage.getItem("ks_sort_best") || 0);

  const choose = (bin) => {
    if (feedback) return;
    const ok = bin === item.bin;
    if (ok) setScore((s) => s + 1);
    setFeedback({ ok, why: item.why });
    setTimeout(() => {
      setFeedback(null);
      setI((n) => n + 1);
      if (i + 1 >= deck.length) localStorage.setItem("ks_sort_best", String(Math.max(best, score + (ok ? 1 : 0))));
    }, 1300);
  };

  const restart = () => { setDeck(shuffle(ITEMS)); setI(0); setScore(0); };

  if (done) {
    return (
      <div className="bg-white rounded-3xl border border-[rgba(20,40,26,.08)] p-8 text-center" data-testid="sort-game-result">
        <Trophy className="mx-auto text-[var(--marigold)]" size={44} />
        <div className="mt-3 font-display text-4xl font-bold">{score}/{deck.length}</div>
        <p className="mt-2 text-[var(--ink-soft)]">{score >= 7 ? t("Segregation champion! Your kabadiwala will thank you.") : t("Nice try — sorting gets easier with practice.")}</p>
        <p className="mt-1 text-xs text-[var(--ink-soft)]">{t("Best score on this device")}: {Math.max(best, score)}</p>
        <button onClick={restart} className="btn-leaf mt-6 text-sm" data-testid="sort-game-restart"><RotateCcw size={15} /> {t("Play again")}</button>
      </div>
    );
  }

  const Icon = item.icon;
  return (
    <div className="bg-white rounded-3xl border border-[rgba(20,40,26,.08)] p-5 sm:p-7" data-testid="sort-game">
      <div className="flex justify-between text-xs font-semibold text-[var(--ink-soft)]">
        <span>{tf("Item {n} of {total}", { n: i + 1, total: deck.length })}</span>
        <span data-testid="sort-game-score">{t("Score")}: {score}</span>
      </div>
      <div className="h-1.5 bg-[var(--paper-2)] rounded-full mt-2 overflow-hidden">
        <div className="h-full bg-[var(--leaf)] transition-[width] duration-500" style={{ width: `${(i / deck.length) * 100}%` }} />
      </div>
      <div className={`mt-7 flex flex-col items-center ${feedback ? (feedback.ok ? "pop" : "shake") : ""}`} key={i}>
        <div className="w-24 h-24 rounded-[28px] bg-[var(--leaf-soft)] flex items-center justify-center floaty">
          <Icon size={44} className="text-[var(--leaf-dark)]" />
        </div>
        <div className="mt-4 font-display text-2xl font-bold" data-testid="sort-game-item">{t(item.name)}</div>
        <div className={`mt-2 min-h-[40px] text-sm text-center max-w-xs ${feedback ? (feedback.ok ? "text-[var(--leaf-dark)]" : "text-[var(--terracotta)]") : "text-[var(--ink-soft)]"}`}>
          {feedback ? `${feedback.ok ? t("Correct!") : t("Not quite.")} ${t(feedback.why)}` : t("Which bin does it go in?")}
        </div>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-3">
        {BINS.map((b) => (
          <button key={b.key} onClick={() => choose(b.key)} disabled={!!feedback} data-testid={`sort-bin-${b.key}`}
            className={`${b.color} text-white rounded-2xl px-2 py-4 text-center transition-transform hover:-translate-y-1 active:scale-95 disabled:opacity-70`}>
            <div className="text-xs sm:text-sm font-bold leading-tight">{t(b.label)}</div>
            <div className="text-[10px] sm:text-xs opacity-80 mt-1">{t(b.hint)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
